"use strict"

import React, { useContext, useEffect, useState } from "react"
import { FormattedMessage } from "react-intl"
import { Button } from "react-bootstrap"

import { API_STATUS } from "../../../shared_components/widgets/utils"
import PageLoadingPanel from "../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../shared_components/widgets/PageErrorPanel"
import { LanzaTable } from "../../../shared_components/widgets/LanzaTable"
import { LocaleFormattedDate } from "../../internationalization/LocaleFormatted"
import ModalViewInventoryItem from "./ModalViewInventoryItem"
import ModalDepleteInventoryItem from "./ModalDepleteInventoryItem"
import ModalAddInventoryItem from "./ModalAddInventoryItem"
import { InventoryConfigContext } from "../../../config/InventoryConfigProvider"

export default function InventoryItemsManageTable({ inventory_focus, grouped_data }) {
  const {
    user_permissions = [],
    getInventoryItems,
    statusInventoryItems,
  } = useContext(InventoryConfigContext)
  const [focused_data, setFocusedData] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)
  const [viewItemModal, setViewItemModal] = useState(false)
  const [depleteItemModal, setDepleteItemModal] = useState(false)
  const [addItemModal, setAddItemModal] = useState(false)

  useEffect(() => {
    if (inventory_focus) {
      setFocusedData(grouped_data.filter((inv) => inv.sku["inventory_type"].name === inventory_focus.name))
    } else {
      setFocusedData(grouped_data)
    }
  }, [inventory_focus, grouped_data])

  const onModalSuccess_alterItem = (_) => {
    setAddItemModal(false)
    setDepleteItemModal(false)
    setSelectedItem(null)
    getInventoryItems()
  }

  return (
    <div>
      {statusInventoryItems === API_STATUS.LOADING && <PageLoadingPanel />}

      {statusInventoryItems === API_STATUS.ERROR && <PageErrorPanel attemptReload={getInventoryItems} />}

      {statusInventoryItems === API_STATUS.SUCCESS &&
        (focused_data.length ? (
          <>
            <LanzaTable
              className={"table table-bordered table-hover tbl_lanzatable"}
              style={{ marginBottom: "0px" }}
              columns={[
                {
                  // Build our expander column
                  id: "expander", // Make sure it has an ID
                  Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
                    <span {...getToggleAllRowsExpandedProps()}>
                      {isAllRowsExpanded ? (
                        <span className={"fa fa-caret-down"} />
                      ) : (
                        <span className={"fa fa-caret-right"} />
                      )}
                    </span>
                  ),
                  Cell: ({ row }) =>
                    // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
                    // to build the toggle for expanding a row
                    row.canExpand ? (
                      <span
                        {...row.getToggleRowExpandedProps({
                          style: {
                            // We can even use the row.depth property
                            // and paddingLeft to indicate the depth
                            // of the row
                            color: "purple",
                            paddingLeft: `${row.depth * 2}rem`,
                          },
                        })}
                      >
                        {row.isExpanded ? (
                          <span className={"fa fa-caret-down"} />
                        ) : (
                          <span className={"fa fa-caret-right"} />
                        )}
                      </span>
                    ) : null,
                },
                {
                  Header: <FormattedMessage id={"general.name"} />,
                  accessor: "sku.name",
                },
                {
                  Header: <FormattedMessage id={"general.description"} />,
                  accessor: "sku.description",
                },
                {
                  Header: <FormattedMessage id={"stock.lot"} />,
                  accessor: "lot_no",
                },
                {
                  Header: <FormattedMessage id={"stock.uom"} />,
                  accessor: "uom",
                },
                {
                  Header: <FormattedMessage id={"inventory.available_quantity"} />,
                  accessor: "available_quantity",
                },
                {
                  Header: <FormattedMessage id={"stock.retest_date"} />,
                  style: { minWidth: "115px" },
                  accessor: "retest_date",
                  Cell: ({ value }) => {
                    return <LocaleFormattedDate date={value} shouldParse={true} showTime={false} />
                  },
                  disableFilters: true,
                },
                {
                  Header: <FormattedMessage id={"inventory.last_received_date"} />,
                  style: { minWidth: "115px" },
                  accessor: "last_received_date",
                  Cell: ({ value }) => {
                    return <LocaleFormattedDate date={value} shouldParse={true} />
                  },
                  disableFilters: true,
                },
                {
                  Header: "",
                  id: "actions",
                  className: "text-center",
                  style: { minWidth: "100px" },
                  Cell: ({ row }) => {
                    const row_data = row.original
                    return row.canExpand ? (
                      <></>
                    ) : (
                      <>
                        <Button
                          variant={"outline-primary"}
                          size={"sm"}
                          onClick={(e) => {
                            e.preventDefault()
                            setSelectedItem(row_data)
                            setViewItemModal(true)
                          }}
                        >
                          <span className={"fa fa-eye"} />
                          &nbsp;&nbsp;
                          <FormattedMessage
                            id={"general.view"}
                            description={"View Inventory Transaction Logs"}
                          />
                        </Button>
                        &nbsp;
                        <Button
                          variant={"outline-primary"}
                          size={"sm"}
                          disabled={!user_permissions.includes("change_inventoryitem")}
                          onClick={(e) => {
                            e.preventDefault()
                            setSelectedItem(row_data)
                            setAddItemModal(true)
                          }}
                        >
                          <span className={"fa fa-plus"} />
                          &nbsp;&nbsp;
                          <FormattedMessage
                            id={"general.add"}
                            description={"Increase Quantity of Inventory Item"}
                          />
                        </Button>
                        {row_data.available_quantity > 0 && (
                          <>
                            &nbsp;
                            <Button
                              variant={"outline-primary"}
                              size={"sm"}
                              style={{ margin: "1px" }}
                              disabled={!user_permissions.includes("change_inventoryitem")}
                              onClick={(e) => {
                                e.preventDefault()
                                setSelectedItem(row_data)
                                setDepleteItemModal(true)
                              }}
                            >
                              <span className={"fa fa-minus text-danger"} />
                              &nbsp;&nbsp;
                              <span className={"text-danger"}>
                                <FormattedMessage
                                  id={"stock.deplete"}
                                  description={"Manually Deplete Inventory Item"}
                                />
                              </span>
                            </Button>
                          </>
                        )}
                      </>
                    )
                  },
                  disableSortBy: true,
                  disableFilters: true,
                },
              ]}
              data={focused_data}
              allowSorting={true}
              allowFilters={true}
              initialTableOptions={{
                sortBy: [{ id: "id", desc: true }],
                pageSize: 10,
              }}
            />
          </>
        ) : (
          <div className={"center_middle"} style={{ height: "200px" }}>
            <div align={"center"}>
              <i className={"fa fa-info-circle"} style={{ fontSize: "45px" }} />
              <h4>
                <FormattedMessage id={"stock.none.in.system"} />
              </h4>
            </div>
          </div>
        ))}

      {/*----- Modals -----*/}
      {viewItemModal && (
        <ModalViewInventoryItem
          inventory_item={selectedItem}
          show={viewItemModal}
          onHide={() => {
            setSelectedItem(null)
            setViewItemModal(false)
          }}
        />
      )}

      {depleteItemModal && selectedItem && user_permissions.includes("change_inventoryitem") && (
        <ModalDepleteInventoryItem
          inventory_item={selectedItem}
          show={depleteItemModal}
          onHide={() => {
            setSelectedItem(null)
            setDepleteItemModal(false)
          }}
          onSuccess={onModalSuccess_alterItem}
        />
      )}

      {addItemModal && selectedItem && user_permissions.includes("change_inventoryitem") && (
        <ModalAddInventoryItem
          inventory_item={selectedItem}
          show={addItemModal}
          onHide={() => {
            setSelectedItem(null)
            setAddItemModal(false)
          }}
          onSuccess={onModalSuccess_alterItem}
        />
      )}
    </div>
  )
}
